import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Settings, 
  Send, 
  BarChart3, 
  Users, 
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff,
  MapPin,
  Link as LinkIcon,
  CreditCard,
  QrCode,
  Upload,
  X,
  Calendar,
  Bell
} from 'lucide-react';
import { adminAPI, uploadAPI } from '../../services/api';
import api from '../../services/api';
import { toast } from 'react-toastify';
import LocationMapSelector from './LocationMapSelector';
import { useGoogleAnalytics } from '../../hooks/useGoogleAnalytics';

const WhatsAppManagement = () => {
  const { trackEvent, trackButtonClick } = useGoogleAnalytics();
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Settings state
  const [settings, setSettings] = useState({
    whatsapp_enabled: false,
    whatsapp_phone_number_id: '',
    whatsapp_business_account_id: '',
    whatsapp_access_token: '',
    whatsapp_verify_token: '',
    whatsapp_app_id: '',
    whatsapp_business_name: 'Niswa Info Tech',
    whatsapp_webhook_url: '',
    whatsapp_notifications_enabled: true,
    whatsapp_order_notifications_enabled: true,
    whatsapp_payment_notifications_enabled: true,
    whatsapp_chat_sound_enabled: true
  });

  // Messaging state
  const [messageForm, setMessageForm] = useState({
    phoneNumber: '',
    message: '',
    messageType: 'text'
  });

  // Templates state (for notification templates only)
  
  const [templates, setTemplates] = useState([]);

  // Notification templates state
  const [notificationTemplates, setNotificationTemplates] = useState({});

  useEffect(() => {
    fetchSettings();
    fetchTemplates();
    fetchManagement();
    fetchNotificationTemplates();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getSettings();
      if (response.data && response.data.data && response.data.data.settings) {
        const backendSettings = response.data.data.settings;
        
        // Map WhatsApp settings
        const whatsappSettings = {};
        Object.keys(backendSettings).forEach(key => {
          if (key.startsWith('whatsapp_')) {
            whatsappSettings[key] = backendSettings[key].value || backendSettings[key];
          }
        });
        
        setSettings(prev => ({ ...prev, ...whatsappSettings }));
      }
    } catch (error) {
      console.error('Error fetching WhatsApp settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/whatsapp-templates');
      if (response.data.success) {
        // Handle the correct response structure from whatsapp-templates route
        const templatesData = response.data.data.templates;
        setTemplates(Array.isArray(templatesData) ? templatesData : []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchNotificationTemplates = async () => {
    try {
      const response = await api.get('/whatsapp/ecommerce-settings');
      if (response.data.success) {
        setNotificationTemplates(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching notification templates:', error);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    setSaveStatus('');
    
    try {
      const whatsappSettings = {};
      Object.keys(settings).forEach(key => {
        if (key.startsWith('whatsapp_')) {
          whatsappSettings[key] = settings[key];
        }
      });

      await adminAPI.updateSettings(whatsappSettings);
      
      // Track WhatsApp settings save
      trackEvent('whatsapp_settings_saved', {
        settings_count: Object.keys(whatsappSettings).length,
        whatsapp_enabled: whatsappSettings.whatsapp_enabled
      });
      
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving WhatsApp settings:', error);
      setSaveStatus('error');
    } finally {
      setLoading(false);
    }
  };

  // Management state (location, contacts, payments, polls, events)
  const [management, setManagement] = useState({
    location: {
      address: '',
      latitude: '',
      longitude: '',
    },
    contacts: [],
    payment: {
      link: '',
      qr: '',
      details: ''
    },
    polls: [],
    events: []
  });

  const [newContact, setNewContact] = useState({ name: '', phone: '' });
  const [newPoll, setNewPoll] = useState({ question: '' });
  const [newEvent, setNewEvent] = useState({ title: '' });
  const [qrFile, setQrFile] = useState(null);
  const [qrPreview, setQrPreview] = useState('');
  const [showLocationMap, setShowLocationMap] = useState(false);

  const fetchManagement = async () => {
    try {
      const res = await api.get('/whatsapp/management');
      if (res.data?.success) {
        setManagement(prev => ({ ...prev, ...res.data.data }));
      }
    } catch (e) {
      console.error('Error loading WhatsApp management:', e);
    }
  };

  const saveManagement = async () => {
    setLoading(true);
    try {
      await api.put('/whatsapp/management', management);
      toast?.success?.('Saved');
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (e) {
      console.error('Save management failed:', e);
      setSaveStatus('error');
    } finally {
      setLoading(false);
    }
  };


  const addContact = () => {
    if (!newContact.name || !newContact.phone) return;
    setManagement(prev => ({ ...prev, contacts: [...(prev.contacts || []), { ...newContact }] }));
    setNewContact({ name: '', phone: '' });
  };
  const removeContact = (index) => {
    setManagement(prev => ({ ...prev, contacts: prev.contacts.filter((_, i) => i !== index) }));
  };
  const addPoll = () => {
    if (!newPoll.question) return;
    setManagement(prev => ({ ...prev, polls: [...(prev.polls || []), { ...newPoll }] }));
    setNewPoll({ question: '' });
  };
  const removePoll = (index) => {
    setManagement(prev => ({ ...prev, polls: prev.polls.filter((_, i) => i !== index) }));
  };
  const addEvent = () => {
    if (!newEvent.title) return;
    setManagement(prev => ({ ...prev, events: [...(prev.events || []), { ...newEvent }] }));
    setNewEvent({ title: '' });
  };
  const removeEvent = (index) => {
    setManagement(prev => ({ ...prev, events: prev.events.filter((_, i) => i !== index) }));
  };

  const handleQrFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') {
        console.log('QR file selected:', file.name, file.type, file.size);
      }
      setQrFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') {
          console.log('QR preview generated:', e.target.result.substring(0, 50) + '...');
        }
        setQrPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadQrCode = async () => {
    if (!qrFile) return;
    
    setLoading(true);
    try {
      if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') {
        console.log('Uploading QR code...');
      }
      
      // Create form data - let the upload service automatically choose S3 or local based on admin settings
      const formData = new FormData();
      formData.append('image', qrFile);
      // Note: Removed forceLocal: 'true' to respect S3 settings
      // Now when S3 is enabled in admin panel → uploads go to S3
      // When S3 is disabled in admin panel → uploads go to local server
      
      const response = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') {
        console.log('Upload response:', response.data);
      }
      
      if (response.data?.success) {
        const qrUrl = response.data.data.url;
        if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') {
          console.log('QR URL received:', qrUrl);
        }
        
        setManagement(prev => ({ 
          ...prev, 
          payment: { 
            ...(prev.payment || {}), 
            qr: qrUrl 
          } 
        }));
        setQrFile(null);
        setQrPreview('');
        toast.success('QR code uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading QR code:', error);
      toast.error('Failed to upload QR code: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const removeQrCode = () => {
    setManagement(prev => ({ 
      ...prev, 
      payment: { 
        ...(prev.payment || {}), 
        qr: '' 
      } 
    }));
    setQrFile(null);
    setQrPreview('');
  };

  const handleLocationSelect = (location) => {
    setManagement(prev => ({ 
      ...prev, 
      location: { 
        address: location.address,
        latitude: location.lat,
        longitude: location.lng
      } 
    }));
    setShowLocationMap(false);
    toast.success('Location updated successfully!');
  };

  const renderManagementTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <MapPin className="w-5 h-5 mr-2" />
          Location
        </h3>
        
        {/* Current Location Display */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Company Address</label>
            <input
              type="text"
              value={management.location?.address || ''}
              onChange={(e) => setManagement(prev => ({ ...prev, location: { ...(prev.location||{}), address: e.target.value } }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter address"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Latitude, Longitude</label>
            <input
              type="text"
              value={`${management.location?.latitude || ''}, ${management.location?.longitude || ''}`}
              onChange={(e) => {
                const [lat, lng] = e.target.value.split(',').map(s => s.trim());
                setManagement(prev => ({ ...prev, location: { ...(prev.location||{}), latitude: lat || '', longitude: lng || '' } }));
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="19.0760, 72.8777"
            />
          </div>
        </div>

        {/* Map Selector Button */}
        <div className="mb-4">
          <button
            onClick={() => setShowLocationMap(!showLocationMap)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <MapPin className="w-4 h-4 mr-2" />
            {showLocationMap ? 'Hide Map' : 'Show Map & Search'}
          </button>
        </div>

        {/* Map Selector */}
        {showLocationMap && (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
            <LocationMapSelector
              initialAddress={management.location?.address || ''}
              initialLat={management.location?.latitude || null}
              initialLng={management.location?.longitude || null}
              onLocationSelect={handleLocationSelect}
            />
          </div>
        )}

        {/* Current Location Summary */}
        {management.location?.address && (
          <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <h4 className="font-medium text-green-800 dark:text-green-200 mb-2 flex items-center">
              <MapPin className="w-4 h-4 mr-2" />
              Current Company Location
            </h4>
            <div className="text-sm text-green-700 dark:text-green-300 space-y-1">
              <div><strong>Address:</strong> {management.location.address}</div>
              {management.location.latitude && management.location.longitude && (
                <div><strong>Coordinates:</strong> {management.location.latitude}, {management.location.longitude}</div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Users className="w-5 h-5 mr-2" />
          Contacts
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <input
            type="text"
            value={newContact.name}
            onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Name"
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
          />
          <input
            type="text"
            value={newContact.phone}
            onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
            placeholder="Phone with country code"
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
          />
          <button onClick={addContact} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Add</button>
        </div>
        <div className="space-y-2">
          {(management.contacts || []).map((c, i) => (
            <div key={i} className="flex items-center justify-between p-2 border rounded dark:border-gray-700">
              <div className="text-sm text-gray-800 dark:text-gray-200">{c.name} — {c.phone}</div>
              <button onClick={() => removeContact(i)} className="text-red-600 text-xs">Remove</button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <CreditCard className="w-5 h-5 mr-2" />
          Payment
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300 flex items-center">
              <LinkIcon className="w-4 h-4 mr-1" /> Payment Link
            </label>
            <input 
              type="text" 
              value={management.payment?.link || ''} 
              onChange={(e) => setManagement(prev => ({...prev, payment: {...(prev.payment||{}), link: e.target.value}}))} 
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
              placeholder="https://..." 
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300 flex items-center">
              <QrCode className="w-4 h-4 mr-1" /> Payment QR Code
            </label>
            <div className="space-y-2">
              {/* QR Code URL Input */}
              <input 
                type="text" 
                value={management.payment?.qr || ''} 
                onChange={(e) => setManagement(prev => ({...prev, payment: {...(prev.payment||{}), qr: e.target.value}}))} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                placeholder="https://.../qr.png" 
              />
              
              {/* QR Code Upload */}
              <div className="flex items-center space-x-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleQrFileChange}
                  className="hidden"
                  id="qr-upload"
                />
                <label
                  htmlFor="qr-upload"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer text-center text-sm"
                >
                  <Upload className="w-4 h-4 inline mr-1" />
                  Upload QR Code
                </label>
                {qrFile && (
                  <button
                    onClick={uploadQrCode}
                    disabled={loading}
                    className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                  >
                    {loading ? 'Uploading...' : 'Upload'}
                  </button>
                )}
              </div>
              
              {/* QR Code Preview */}
              {(qrPreview || management.payment?.qr) && (
                <div className="relative inline-block">
                  <img 
                    src={qrPreview || management.payment?.qr} 
                    alt="QR Code" 
                    className="w-16 h-16 border rounded-lg object-cover"
                    onError={(e) => {
                      console.error('QR image failed to load:', e.target.src);
                      e.target.style.display = 'none';
                      
                      // Try local fallback if it's an S3 URL
                      if (e.target.src.includes('s3.ap-south-1.amazonaws.com')) {
                        const localUrl = e.target.src.replace('https://niswainfotech.s3.ap-south-1.amazonaws.com', 'http://localhost:5000');
                        if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('Trying local fallback URL:', localUrl);
                        
                        const fallbackImg = document.createElement('img');
                        fallbackImg.src = localUrl;
                        fallbackImg.alt = 'QR Code';
                        fallbackImg.className = 'w-16 h-16 border rounded-lg object-cover';
                        fallbackImg.onError = () => {
                          fallbackImg.style.display = 'none';
                          // Show icon fallback
                          const fallback = document.createElement('div');
                          fallback.className = 'w-16 h-16 border rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-500';
                          fallback.innerHTML = '<svg class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M3 4h18v2H3V4zm0 7h18v2H3v-2zm0 7h18v2H3v-2z"/></svg>';
                          e.target.parentNode.appendChild(fallback);
                        };
                        e.target.parentNode.appendChild(fallbackImg);
                      } else {
                        // Show icon fallback
                        const fallback = document.createElement('div');
                        fallback.className = 'w-16 h-16 border rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-500';
                        fallback.innerHTML = '<svg class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M3 4h18v2H3V4zm0 7h18v2H3v-2zm0 7h18v2H3v-2z"/></svg>';
                        e.target.parentNode.appendChild(fallback);
                      }
                    }}
                    onLoad={() => { if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('QR image loaded successfully'); }}
                  />
                  <button
                    onClick={removeQrCode}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    title="Remove QR Code"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              

            </div>
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Payment Details</label>
          <textarea 
            value={management.payment?.details || ''} 
            onChange={(e) => setManagement(prev => ({...prev, payment: {...(prev.payment||{}), details: e.target.value}}))} 
            rows={3} 
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
            placeholder="Account/UPI details" 
          />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2" />
          Polls & Events
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
          <div>
            <h4 className="text-md font-medium mb-3 text-purple-600 dark:text-purple-400 flex items-center">
              <BarChart3 className="w-4 h-4 mr-2" />
              Polls
            </h4>
            <div className="flex items-center space-x-2 mb-3">
              <input 
                type="text" 
                value={newPoll.question} 
                onChange={(e) => setNewPoll({question: e.target.value})} 
                placeholder="Poll question" 
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
              />
              <button 
                onClick={addPoll} 
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
              >
                Add
              </button>
            </div>
            <div className="space-y-2">
              {(management.polls || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                  <div className="text-sm text-gray-800 dark:text-gray-200 font-medium">{p.question}</div>
                  <button 
                    onClick={() => removePoll(i)} 
                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-xs font-medium"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-md font-medium mb-3 text-orange-600 dark:text-orange-400 flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              Events
            </h4>
            <div className="flex items-center space-x-2 mb-3">
              <input 
                type="text" 
                value={newEvent.title} 
                onChange={(e) => setNewEvent({title: e.target.value})} 
                placeholder="Event title" 
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent" 
              />
              <button 
                onClick={addEvent} 
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
              >
                Add
              </button>
            </div>
            <div className="space-y-2">
              {(management.events || []).map((ev, i) => (
                <div key={i} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-orange-50 dark:bg-orange-900/20">
                  <div className="text-sm text-gray-800 dark:text-gray-200 font-medium">{ev.title}</div>
                  <button 
                    onClick={() => removeEvent(i)} 
                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-xs font-medium"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-2 flex justify-end">
        <button onClick={saveManagement} disabled={loading} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2">
          <Save className="w-4 h-4" />
          <span>Save Management</span>
        </button>
      </div>
    </div>
  );

  const testConnection = async () => {
    setLoading(true);
    setTestResult(null);
    
    try {
      const response = await api.post('/whatsapp/test');
      
      // Track WhatsApp connection test
      trackEvent('whatsapp_connection_tested', {
        test_success: response.data.success,
        test_result: response.data.message
      });
      
      setTestResult({
        success: response.data.success,
        message: response.data.message
      });
      
      // Show success/failure toast for immediate feedback
      if (response.data.success) {
        toast.success(`WhatsApp Connection Test Successful: ${response.data.message}`);
      } else {
        toast.error(`WhatsApp Connection Test Failed: ${response.data.message}`);
      }
    } catch (error) {
      console.error('WhatsApp connection test error:', error);
      
      let errorMessage = 'Connection test failed';
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Track failed test
      trackEvent('whatsapp_connection_tested', {
        test_success: false,
        test_result: errorMessage
      });
      
      setTestResult({
        success: false,
        message: errorMessage
      });
      
      // Show toast notification for immediate feedback
      toast.error(`WhatsApp Connection Test Failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    setLoading(true);
    
    try {
      const response = await api.post('/whatsapp/send/text', {
        phoneNumber: messageForm.phoneNumber,
        message: messageForm.message
      });
      
      // Track WhatsApp test message sent
      trackEvent('whatsapp_test_message_sent', {
        phone_number: messageForm.phoneNumber,
        message_length: messageForm.message.length,
        success: response.data.success
      });
      
      if (response.data.success) {
        alert('Message sent successfully!');
        setMessageForm({
          phoneNumber: '',
          message: '',
          messageType: 'text'
        });
      }
    } catch (error) {
      alert('Failed to send message: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };


  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleMessageFormChange = (key, value) => {
    setMessageForm(prev => ({ ...prev, [key]: value }));
  };


  const renderSettingsTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Settings className="w-5 h-5 mr-2" />
          WhatsApp Configuration
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="whatsapp_enabled"
              checked={settings.whatsapp_enabled}
              onChange={(e) => handleSettingChange('whatsapp_enabled', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="whatsapp_enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Enable WhatsApp Cloud API
            </label>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Phone Number ID</label>
            <input
              type="text"
              value={settings.whatsapp_phone_number_id}
              onChange={(e) => handleSettingChange('whatsapp_phone_number_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter Phone Number ID"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Business Account ID</label>
            <input
              type="text"
              value={settings.whatsapp_business_account_id}
              onChange={(e) => handleSettingChange('whatsapp_business_account_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter Business Account ID"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Required for template submission. Found in Meta Business Manager → WhatsApp Manager → API Setup
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Access Token</label>
            <div className="relative">
              <input
                type={showAccessToken ? 'text' : 'password'}
                value={settings.whatsapp_access_token}
                onChange={(e) => handleSettingChange('whatsapp_access_token', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                placeholder="Enter Access Token"
              />
              <button
                type="button"
                onClick={() => setShowAccessToken(!showAccessToken)}
                className="absolute right-2 top-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                {showAccessToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Verify Token</label>
            <input
              type="text"
              value={settings.whatsapp_verify_token}
              onChange={(e) => handleSettingChange('whatsapp_verify_token', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter Verify Token"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">App ID</label>
            <input
              type="text"
              value={settings.whatsapp_app_id}
              onChange={(e) => handleSettingChange('whatsapp_app_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter App ID"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Business Name</label>
            <input
              type="text"
              value={settings.whatsapp_business_name}
              onChange={(e) => handleSettingChange('whatsapp_business_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter Business Name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Webhook URL</label>
            <input
              type="text"
              value={settings.whatsapp_webhook_url}
              onChange={(e) => handleSettingChange('whatsapp_webhook_url', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://yourdomain.com/api/whatsapp/webhook"
            />
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              For ngrok: https://xxxxxxxx.ngrok.io/api/whatsapp/webhook
            </div>
          </div>

        </div>

        <div className="mt-6 space-y-4">
          <h4 className="font-medium text-gray-900 dark:text-white">Notification Settings</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="whatsapp_notifications_enabled"
                checked={settings.whatsapp_notifications_enabled}
                onChange={(e) => handleSettingChange('whatsapp_notifications_enabled', e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="whatsapp_notifications_enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Enable WhatsApp Notifications
              </label>
            </div>
            
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="whatsapp_order_notifications_enabled"
                checked={settings.whatsapp_order_notifications_enabled}
                onChange={(e) => handleSettingChange('whatsapp_order_notifications_enabled', e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="whatsapp_order_notifications_enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Order Notifications
              </label>
            </div>
            
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="whatsapp_payment_notifications_enabled"
                checked={settings.whatsapp_payment_notifications_enabled}
                onChange={(e) => handleSettingChange('whatsapp_payment_notifications_enabled', e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="whatsapp_payment_notifications_enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Payment Notifications
              </label>
            </div>
            
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="whatsapp_chat_sound_enabled"
                checked={settings.whatsapp_chat_sound_enabled}
                onChange={(e) => handleSettingChange('whatsapp_chat_sound_enabled', e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="whatsapp_chat_sound_enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Play sound on new chat message (Admin Chat)
              </label>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center space-x-4">
          <button
            onClick={testConnection}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Test Connection'}
          </button>
          
          {testResult && (
            <div className={`p-4 rounded-lg border ${
              testResult.success 
                ? 'bg-green-50 border-green-200 text-green-800' 
                : 'bg-red-50 border-red-200 text-red-800'
            }`}>
              <div className="flex items-start space-x-3">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <div className="font-medium text-sm">
                    {testResult.success ? 'Connection Test Successful' : 'Connection Test Failed'}
                  </div>
                  <div className="text-sm mt-1">{testResult.message}</div>
                  {testResult.data && (
                    <div className="text-xs mt-2 space-y-1">
                      {testResult.data.phoneNumber && (
                        <div>Phone: {testResult.data.phoneNumber}</div>
                      )}
                      {testResult.data.verifiedName && (
                        <div>Name: {testResult.data.verifiedName}</div>
                      )}
                      <div>Status: {testResult.data.enabled ? 'Enabled' : 'Disabled'} | {testResult.data.configured ? 'Configured' : 'Not Configured'}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={saveSettings}
            disabled={loading}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>Save Settings</span>
          </button>
        </div>

        {saveStatus && (
          <div className={`mt-4 p-4 rounded-lg ${
            saveStatus === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {saveStatus === 'success' ? 'Settings saved successfully!' : 'Failed to save settings'}
          </div>
        )}
      </div>
    </div>
  );

  const renderMessagingTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Send className="w-5 h-5 mr-2" />
          Send WhatsApp Message
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Phone Number</label>
            <input
              type="text"
              value={messageForm.phoneNumber}
              onChange={(e) => handleMessageFormChange('phoneNumber', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="+1234567890"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Message</label>
            <textarea
              value={messageForm.message}
              onChange={(e) => handleMessageFormChange('message', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={4}
              placeholder="Enter your message..."
            />
          </div>
          
          <button
            onClick={sendMessage}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <Send className="w-4 h-4" />
            <span>Send Message</span>
          </button>
        </div>
      </div>
    </div>
  );


  const updateNotificationTemplate = async (key, value) => {
    try {
      const response = await api.put('/whatsapp/ecommerce-settings', { [key]: value });
      if (response.data.success) {
        setNotificationTemplates(prev => ({ ...prev, [key]: value }));
        setSaveStatus('success');
        setTimeout(() => setSaveStatus(''), 3000);
      }
    } catch (error) {
      console.error('Error updating notification template:', error);
      setSaveStatus('error');
    }
  };

  const renderNotificationTemplatesTab = () => {
    // All notification types from email settings plus e-commerce specific ones
    const allNotificationTypes = [
      // E-commerce notifications
      { value: 'abandoned_cart', label: 'Abandoned Cart Reminder', category: 'E-commerce' },
      { value: 'order_confirmed', label: 'Order Confirmed', category: 'E-commerce' },
      { value: 'payment_received', label: 'Payment Received', category: 'E-commerce' },
      { value: 'processing', label: 'Order Processing', category: 'E-commerce' },
      { value: 'shipped', label: 'Order Shipped', category: 'E-commerce' },
      { value: 'delivered', label: 'Order Delivered', category: 'E-commerce' },
      { value: 'cancelled', label: 'Order Cancelled', category: 'E-commerce' },
      { value: 'catalog', label: 'Catalog Sharing', category: 'E-commerce' },
      
      // User account notifications
      { value: 'account_creation', label: 'Account Creation', category: 'User Account' },
      { value: 'login_alert', label: 'Login Alert', category: 'User Account' },
      { value: 'multi_device_warning', label: 'Multi-Device Warning', category: 'User Account' },
      { value: 'password_reset', label: 'Password Reset', category: 'User Account' },
      { value: 'otp_verification', label: 'OTP Verification', category: 'User Account' },
      
      // Contact and inquiry notifications
      { value: 'contact_notification', label: 'Contact Form Submission', category: 'Contact' },
      { value: 'appointment_confirmation', label: 'Appointment Confirmation', category: 'Contact' },
      { value: 'quote_ready', label: 'Quote Ready', category: 'Contact' },
      
      // Payment notifications
      { value: 'payment_confirmation', label: 'Payment Confirmation', category: 'Payment' },
      { value: 'payment_reminder', label: 'Payment Reminder', category: 'Payment' },
      { value: 'split_payment_reminder', label: 'Split Payment Reminder', category: 'Payment' },
      { value: 'split_payment_warning', label: 'Split Payment Warning', category: 'Payment' },
      { value: 'split_payment_fine_alert', label: 'Split Payment Fine Alert', category: 'Payment' },
      
      // System notifications
      { value: 'user_notification', label: 'User Notification', category: 'System' },
      { value: 'admin_notification', label: 'Admin Notification', category: 'System' },
      { value: 'system_alert', label: 'System Alert', category: 'System' },
      { value: 'maintenance_notification', label: 'Maintenance Notification', category: 'System' },
      
      // Product and promotion notifications
      { value: 'product_notification', label: 'Product Notification', category: 'Marketing' },
      { value: 'promotion_notification', label: 'Promotion Notification', category: 'Marketing' },
      { value: 'newsletter_notification', label: 'Newsletter Notification', category: 'Marketing' },
      
      // Coupon notifications
      { value: 'coupon_assigned', label: 'Coupon Assigned', category: 'Coupons' },
      { value: 'coupon_created', label: 'Coupon Created', category: 'Coupons' },
      { value: 'coupon_updated', label: 'Coupon Updated', category: 'Coupons' },
      { value: 'coupon_expiry_reminder', label: 'Coupon Expiry Reminder', category: 'Coupons' },
      { value: 'coupon_usage', label: 'Coupon Usage Notification', category: 'Coupons' }
    ];

    // Group notifications by category
    const groupedNotifications = allNotificationTypes.reduce((acc, notification) => {
      if (!acc[notification.category]) {
        acc[notification.category] = [];
      }
      acc[notification.category].push(notification);
      return acc;
    }, {});

    return (
    <div className="space-y-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
            WhatsApp Notification Templates
        </h3>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Configure which templates to use for different types of WhatsApp notifications. 
            Select a template for each notification type or leave empty to use default messages.
          </p>
          </div>
          
        {Object.entries(groupedNotifications).map(([category, notifications]) => (
          <div key={category} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h4 className="text-lg font-medium text-gray-900 dark:text-white">{category}</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Configure templates for {category.toLowerCase()} notifications
              </p>
          </div>
          
            <div className="p-4 sm:p-6 space-y-4">
              {notifications.map(notification => (
                <div key={notification.value} className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      {notification.label}
                    </label>
            <select
                      value={notificationTemplates[`${notification.value}_template`] || ''}
                      onChange={(e) => updateNotificationTemplate(`${notification.value}_template`, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                    >
                      <option value="">Use default message</option>
                      {templates && templates.length > 0 ? (
                        templates.filter(t => t.is_active === 1 && (t.meta_status === 'APPROVED' || t.meta_status === null)).length > 0 ? (
                          templates.filter(t => t.is_active === 1 && (t.meta_status === 'APPROVED' || t.meta_status === null)).map(template => (
                            <option key={template.id} value={template.template_name}>
                              {template.template_name} ({template.template_category}) {template.meta_status === 'APPROVED' ? '✓' : ''}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>No available templates</option>
                        )
                      ) : (
                        <option value="" disabled>Loading templates...</option>
                      )}
            </select>
          </div>
                  <div className="flex items-center">
        <button
                      onClick={() => updateNotificationTemplate(`${notification.value}_enabled`, 
                        notificationTemplates[`${notification.value}_enabled`] === 'true' ? 'false' : 'true')}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium ${
                        notificationTemplates[`${notification.value}_enabled`] === 'true'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                      }`}
                    >
                      {notificationTemplates[`${notification.value}_enabled`] === 'true' ? 
                        <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                      <span>{notificationTemplates[`${notification.value}_enabled`] === 'true' ? 'Enabled' : 'Disabled'}</span>
        </button>
      </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
          <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">Template Variables</h4>
          <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2">
            Use these variables in your templates to dynamically insert data. <strong>Note:</strong> Available templates include both approved templates (marked with ✓) and active templates from the WhatsApp Templates section.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            <div><code className="bg-yellow-100 dark:bg-yellow-800 px-2 py-1 rounded">{'{{order_id}}'}</code> - Order ID</div>
            <div><code className="bg-yellow-100 dark:bg-yellow-800 px-2 py-1 rounded">{'{{amount}}'}</code> - Amount</div>
            <div><code className="bg-yellow-100 dark:bg-yellow-800 px-2 py-1 rounded">{'{{customer_name}}'}</code> - Customer Name</div>
            <div><code className="bg-yellow-100 dark:bg-yellow-800 px-2 py-1 rounded">{'{{item_names}}'}</code> - Item Names</div>
            <div><code className="bg-yellow-100 dark:bg-yellow-800 px-2 py-1 rounded">{'{{total_amount}}'}</code> - Total Amount</div>
            <div><code className="bg-yellow-100 dark:bg-yellow-800 px-2 py-1 rounded">{'{{tracking_number}}'}</code> - Tracking Number</div>
            <div><code className="bg-yellow-100 dark:bg-yellow-800 px-2 py-1 rounded">{'{{tracking_url}}'}</code> - Tracking URL</div>
            <div><code className="bg-yellow-100 dark:bg-yellow-800 px-2 py-1 rounded">{'{{category}}'}</code> - Category</div>
            <div><code className="bg-yellow-100 dark:bg-yellow-800 px-2 py-1 rounded">{'{{products}}'}</code> - Product List</div>
        </div>
      </div>
    </div>
  );
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
          WhatsApp Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage WhatsApp Cloud API integration, messaging, and templates
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex overflow-x-auto scrollbar-hide">
          <div className="flex space-x-2 sm:space-x-8 min-w-max">
            {[
              { id: 'settings', label: 'Settings', icon: Settings },
              { id: 'messaging', label: 'Messaging', icon: Send },
              { id: 'notification-templates', label: 'Notification Templates', icon: Bell },
              { id: 'management', label: 'Management', icon: MessageSquare }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            ))}
          </div>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'settings' && renderSettingsTab()}
      {activeTab === 'messaging' && renderMessagingTab()}
      {activeTab === 'notification-templates' && renderNotificationTemplatesTab()}
      {activeTab === 'management' && renderManagementTab()}
    </div>
  );
};

export default WhatsAppManagement;
